/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2024-2026 (original work) Open Assessment Technologies SA ;
 */

import { isEmpty } from '../../helpers/objectHelper.js';

const STATUS_KEYS = {
    isSupported: 'pages.diagnostic.Results.system_meets_specs',
    isPartiallySupported: 'pages.diagnostic.Results.system_partially_meet_specs',
    isNotSupported: 'pages.diagnostic.Results.system_not_meet_specs'
};

const STATUS_HIERARCHY = {
    isSupported: 'success',
    isPartiallySupported: 'warning',
    isNotSupported: 'alert'
};

const SECURE_BROWSER_VERSION_ERROR_KEY = 'pages.diagnostic.Results.secure_browser_version_error';
const SECURE_BROWSER_LINK_PLACEHOLDER = '{{secureBrowserDownloadUrl}}';

const getStatusMap = (translate) => ({
    isSupported: {
        title: translate(STATUS_KEYS.isSupported),
        hierarchy: STATUS_HIERARCHY.isSupported
    },
    isPartiallySupported: {
        title: translate(STATUS_KEYS.isPartiallySupported),
        hierarchy: STATUS_HIERARCHY.isPartiallySupported
    },
    isNotSupported: {
        title: translate(STATUS_KEYS.isNotSupported),
        hierarchy: STATUS_HIERARCHY.isNotSupported
    }
});

const parseSecureBrowserValue = ({isSecureBrowserEnabled, results, field}) => {
    if (!isSecureBrowserEnabled) {
        return true;
    }
    if (results.isSecureBrowser) {
        if (field === 'version') {
            return results.result.isVersionMet;
        }
        if (field === 'processList') {
            return (results.result?.processList?.length ?? 0) === 0;
        }
        return null;
    }

    return null;
};

const normalizeExternalUrl = (value) => {
    if (!value || typeof value !== 'string') {
        return '';
    }
    try {
        const url = new URL(value);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
            return url.toString();
        }
    } catch (error) {
        return '';
    }
    return '';
};

const buildSecureBrowserVersionErrorContent = (translate, downloadUrl) => {
    const safeDownloadUrl = normalizeExternalUrl(downloadUrl);
    if (!safeDownloadUrl) {
        return '';
    }

    const errorTemplate = translate(
        SECURE_BROWSER_VERSION_ERROR_KEY,
        { d: SECURE_BROWSER_LINK_PLACEHOLDER }
    );
    const versionErrorWithLinkPattern = /^(.*)<a\s+[^>]*>(.*)<\/a>(.*)$/i;
    const match = errorTemplate.match(versionErrorWithLinkPattern);

    if (!match) {
        return translate(SECURE_BROWSER_VERSION_ERROR_KEY, { d: safeDownloadUrl });
    }

    return {
        textBefore: match[1],
        linkText: match[2],
        textAfter: match[3],
        linkUrl: safeDownloadUrl
    };
};

const createBandwidthResultItem = ({ key, title, isVisible, results, translate, configuration, components }) => ({
    title,
    isVisible,
    key,
    get items() {
        const bandwidthResult = results[this.key];
        return [
            {
                title: translate('pages.diagnostic.Results.download'),
                component: components.BandwidthCheckResultItem,
                value: {
                    ...toHumanReadable(bandwidthResult.downloadSpeed, 'speed'),
                    isSupported:
                        configuration[this.key].download.acceptable <=
                        bandwidthResult.downloadSpeed
                }
            },
            {
                title: translate('pages.diagnostic.Results.upload'),
                component: components.BandwidthCheckResultItem,
                value: {
                    ...toHumanReadable(bandwidthResult.uploadSpeed, 'speed'),
                    isSupported:
                        configuration[this.key].upload.acceptable <=
                        bandwidthResult.uploadSpeed
                }
            },
            {
                title: translate('pages.diagnostic.Results.latency'),
                component: components.BandwidthCheckResultItem,
                value: {
                    ...toHumanReadable(bandwidthResult.latency, 'time'),
                    isSupported:
                        configuration[this.key].latency.acceptable >=
                        bandwidthResult.latency
                }
            }
        ];
    },
    get info() {
        const isBandwidthMessageVisible = configuration.showBandwidthMessage !== false;
        return isBandwidthMessageVisible
            ? translate(
                'pages.diagnostic.Results.bandwidth_result_based',
                { d: configuration[this.key].concurrentUsers }
            )
            : '';
    },
    get status() {
        return this.items.every(item => item.value.isSupported)
            ? getStatusMap(translate).isSupported
            : getStatusMap(translate).isNotSupported;
    }
});

const RESULT_ITEMS_CREATORS = [
    ({ results, isRunningAudioTest, isRunningVideoTest, translate, configuration, components }) =>
        createBandwidthResultItem({
            key: 'connectionCompatibility',
            title: translate('pages.diagnostic.Results.bandwidth'),
            isVisible: !isRunningAudioTest && !isRunningVideoTest,
            results,
            translate,
            configuration,
            components
        }),
    ({ results, isRunningAudioTest, translate, configuration, components }) =>
        createBandwidthResultItem({
            key: 'audioCompatibility',
            title: translate('pages.diagnostic.Results.audio_bandwidth', {}, 'Audio bandwidth'),
            isVisible: isRunningAudioTest,
            results,
            translate,
            configuration,
            components
        }),
    ({ results, isRunningVideoTest, translate, configuration, components }) =>
        createBandwidthResultItem({
            key: 'videoCompatibility',
            title: translate('pages.diagnostic.Results.video_bandwidth', {}, 'Video bandwidth'),
            isVisible: isRunningVideoTest,
            results,
            translate,
            configuration,
            components
        }),
    ({ results, translate, components, configuration }) => ({
        isVisible: true,
        title: translate('pages.diagnostic.Results.browser'),
        key: 'browserCompatibility',
        listStyle: 'table',
        get items() {
            return [
                {
                    title: translate('pages.diagnostic.Results.browser'),
                    component: components.BrowserCheckResultItem,
                    value: {
                        name: results[this.key].browser.name,
                        isSupported: results[this.key].result.listed
                    }
                },
                {
                    title: translate('pages.diagnostic.Results.version'),
                    component: components.BrowserCheckResultItem,
                    value: {
                        name: results[this.key].browser.version,
                        isSupported: results[this.key].result.accepted
                    }
                },
                {
                    title: translate('pages.diagnostic.Results.secure_browser_minimum_version'),
                    component: components.SecureBrowserCheckResultItem,
                    itemStyle: 'inline',
                    errorContent: (results[this.key].isSecureBrowser && results[this.key].result?.isVersionMet === false)
                        ? buildSecureBrowserVersionErrorContent(
                            translate,
                            configuration?.secureBrowserCompatibility?.portalDownloadPage
                        )
                        : '',
                    isVisible: results[this.key]?.isSecureBrowserEnabled,
                    value: {
                        name: 'version',
                        isSupported: parseSecureBrowserValue({ isSecureBrowserEnabled: results[this.key]?.isSecureBrowserEnabled, results: results[this.key], field: 'version'})
                    }
                },
                {
                    title: translate('pages.diagnostic.Results.secure_browser_process'),
                    component: components.SecureBrowserCheckResultItem,
                    itemStyle: 'inline',
                    errorContent: results[this.key].isSecureBrowser && (results[this.key].result?.processList?.length ?? 0) > 0 ? translate('pages.diagnostic.Results.secure_browser_process_list_error', { d: results[this.key].result?.processList }) : '',
                    isVisible: results[this.key]?.isSecureBrowserEnabled,
                    value: {
                        name: 'processList',
                        isSupported: parseSecureBrowserValue({ isSecureBrowserEnabled: results[this.key]?.isSecureBrowserEnabled, results: results[this.key], field: 'processList'})
                    }
                }
            ];
        },
        get status() {
            let isPartiallySupported = false;
            let isPartiallySupportedMessage = {};
            if (results[this.key]?.isSecureBrowserEnabled) {
                const secureBrowserDownloadLink = !isEmpty(configuration?.endpointsReachability?.serviceEndpoints?.['TAO Portal'])
                    ? normalizeExternalUrl(configuration?.secureBrowserCompatibility?.portalDownloadPage)
                    : '';
                isPartiallySupportedMessage = {
                    title: translate('pages.diagnostic.Results.secure_browser_partial_msg'),
                    hierarchy: 'warning',
                    actions: secureBrowserDownloadLink ? [{
                        type: 'link',
                        action: { link: secureBrowserDownloadLink },
                        title: translate('pages.diagnostic.Results.secure_browser_partial_btn')
                    }] : []
                };

                isPartiallySupported = this.items.some(item => item.value.isSupported === null);
            }

            let statusMessage = {};
            if (this.items.some(item => item.value.isSupported === false)) {
                statusMessage = getStatusMap(translate)['isNotSupported'];
            } else if (isPartiallySupported) {
                statusMessage = isPartiallySupportedMessage;
            } else {
                statusMessage = getStatusMap(translate)['isSupported'];
            }
            return statusMessage;
        }
    }),
    ({ results, translate, components }) => ({
        isVisible: true,
        key: 'endpointsReachability',
        title: translate('pages.diagnostic.Results.connectivity'),
        listStyle: 'vertical',
        get items() {
            return Object.keys(results.endpointsReachability).map((resultKey) => ({
                title: resultKey,
                component: components.ConnectivityCheckResultItem,
                value: results[this.key][resultKey],
            }));
        },
        get status() {
            return this.items.every(item => item.value.reachable) ? getStatusMap(translate)['isSupported'] : getStatusMap(translate)['isNotSupported'];
        }
    }),
    ({ results, translate }) => ({
        isVisible: true,
        title: translate('pages.diagnostic.Results.webSocketConnection'),
        key: 'webSocketCompatibility',
        get info() {
            return results[this.key].enabled
                ? translate('components.WebSocketCheckResultItem.yes')
                : translate('components.WebSocketCheckResultItem.no');
        },
        get status() {
            return results[this.key].enabled ? getStatusMap(translate)['isSupported'] : getStatusMap(translate)['isNotSupported'];
        },
    }),
    ({ translate, results, isRunningAudioTest, components }) => ({
        isVisible: isRunningAudioTest,
        title: translate('pages.diagnostic.Results.audio'),
        key: 'audio',
        get items() {
            return [
                {
                    title: translate('pages.diagnostic.Results.playback'),
                    component: components.AudioCheckResultItem,
                    value: {
                        isSupported: results[this.key],
                    }
                }
            ];
        },
        get status() {
            return this.items.every(item => item.value.isSupported) ? getStatusMap(translate)['isSupported'] : getStatusMap(translate)['isNotSupported'];
        }
    }),
    ({ translate, results, isRunningVideoTest, components }) => ({
        isVisible: isRunningVideoTest,
        title: translate('pages.diagnostic.Results.video', {}, 'Video'),
        key: 'video',
        get items() {
            return [
                {
                    title: translate('pages.diagnostic.Results.playback'),
                    component: components.AudioCheckResultItem,
                    value: {
                        isSupported: results[this.key],
                    }
                }
            ];
        },
        get status() {
            return this.items.every(item => item.value.isSupported) ? getStatusMap(translate)['isSupported'] : getStatusMap(translate)['isNotSupported'];
        }
    })
];

/**
 * Converts value to human-readable form
 * @param {number} value
 * @param {string} type
 * @returns {object}
 */
function toHumanReadable(value, type) {
    switch (type) {
        case 'speed':
            return humanReadableSpeed(value);
        case 'time':
            return humanReadableTime(value);
        default:
            return { value };
    }
}

function humanReadableSpeed(speed) {
    if (speed === null) {
        return {
            value: 'N/A',
            unit: ''
        };
    }
    const units = ['bytes/s', 'KB/s', 'MB/s'];
    let i = 0;
    while (speed > 1024) {
        speed /= 1024;
        i++;
    }
    return {
        value: speed.toFixed(2),
        unit: units[i]
    };
}

function humanReadableTime(time) {
    const units = ['ms', 's', 'm', 'h', 'd'];
    let i = 0;
    while (time > 1000) {
        time /= 1000;
        i++;
    }
    return {
        value: time.toFixed(2),
        unit: units[i]
    };
}

/**
 * @param {Object} object
 * @returns {Object}
 */
export function omitEmpty(object) {
    const newObject = {};
    for (const key in object) {
        const value = object[key];
        if (value != null) { // skip null & undefined
            newObject[key] = value;
        }
    }
    return newObject;
}

/**
 * @param {Object} results
 * @param {{key: string}} item
 * @returns {boolean}
 */
export const hasResults = (results, item) => {
    const result = results[item.key];
    return result === false || // Allow false as result value
        (result != null && !isEmpty(result));
}

/**
 * @typedef {Object} ResultItem
 * @property {boolean} isVisible
 * @property {string} title
 * @property {string} key
 * @property {boolean} readonly
 * @property {{component: SvelteComponent, title: string, value: {isSupported: boolean, value: (number|string|*)}}[]} items
 *
 * @param {{}} args
 * @param {{}} args.results
 * @param {{}} args.configuration
 * @param {{}} args.components
 * @param {boolean} args.isRunningAudioTest
 * @param {boolean} args.isRunningVideoTest
 * @param {Function} args.translate
 * @returns {ResultItem[]}
 */
export function getDiagnosticResultsItems(args) {
    const { results } = args;
    return RESULT_ITEMS_CREATORS
        .map(createItem => {
            const item = createItem(args);
            const isItemVisible = hasResults(results, item) && item.isVisible;
            return isItemVisible ? item : null;
        })
        .filter(item => !!item);
}
